import { Helmet } from 'react-helmet-async'
import { BreadcrumbsJsonLd } from '@/components/seo/Breadcrumbs'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowRight, CalendarPlus, ArrowLeft } from 'lucide-react'
import { trackEvent } from '@/lib/analytics'
import { SimpleLocationsSection } from '@/components/sections/simple-locations-section'
import { TableOfContents } from '@/components/sections/table-of-contents'

const tocItems = [
  { id: 'introduction-section', title: 'Pregnancy & Postpartum Support' },
  { id: 'who-this-is-for', title: 'Who This Is For' },
  { id: 'common-reasons', title: 'Common Reasons People Seek Support' },
  { id: 'how-acupuncture-helps', title: 'How Acupuncture & Chinese Medicine May Help' },
  { 
    id: 'support-across-trimesters', 
    title: 'Support Across the Trimesters',
    children: [
      { id: 'postpartum-fourth-trimester', title: 'Postpartum & the Fourth Trimester' },
      { id: 'pregnancy-after-loss', title: 'Pregnancy After Loss or Fertility Treatment' },
    ]
  },
  { id: 'what-to-expect', title: 'What to Expect' },
]

export default function PregnancyPostpartumPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const showBackLink = location.state?.fromParent === true
  const showHomepageBackLink = location.state?.fromHomepage === true
  const showAppointmentsBackLink = location.state?.fromAppointmentsPage === true

  const openBooking = (source?: string) => {
    if (source) {
      trackEvent('book_appointment_click', { location: source })
    }
    if ((window as any).openBookingModal) {
      ;(window as any).openBookingModal(source)
    }
  }

  return (
    <main className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Pregnancy & Postpartum Support | Raintree Chinese Medicine</title>
        <meta name="description" content="Pregnancy and postpartum support at Raintree Chinese Medicine. Care through all trimesters and into the postpartum months." />
        <meta property="og:title" content="Pregnancy & Postpartum Support | Raintree Chinese Medicine" />
        <meta property="og:description" content="Pregnancy and postpartum support at Raintree Chinese Medicine. Care through all trimesters and into the postpartum months." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://raintreechinesemedicine.com.au/what-we-help-with/pregnancy-postpartum" />
      </Helmet>
      <h1 className="sr-only">Pregnancy & Postpartum Support</h1>
      <BreadcrumbsJsonLd />
      
      {/* Table of Contents */}
      <TableOfContents items={tocItems} />

      {/* Hero Section */}
      <section className="bg-[rgb(132,137,116)] py-16 lg:py-40 px-4 lg:px-0 relative">
        {showHomepageBackLink && (
          <button
            onClick={() => navigate('/#what-we-help-with-section')}
            className="absolute top-4 left-4 text-white/90 hover:text-white text-sm flex items-center gap-1 transition-colors z-10"
          >
            <ArrowLeft className="h-4 w-4" />
            back to What We Help With
          </button>
        )}
        {showAppointmentsBackLink && !showHomepageBackLink && (
          <button
            onClick={() => navigate('/appointments#areas-of-focus-section')}
            className="absolute top-4 left-4 text-white/90 hover:text-white text-sm flex items-center gap-1 transition-colors z-10"
          >
            <ArrowLeft className="h-4 w-4" />
            back to Appointments and Fees
          </button>
        )}
        {showBackLink && !showHomepageBackLink && !showAppointmentsBackLink && (
          <button
            onClick={() => navigate('/what-we-help-with')}
            className="absolute top-4 left-4 text-white/90 hover:text-white text-sm flex items-center gap-1 transition-colors z-10"
          >
            <ArrowLeft className="h-4 w-4" />
            back to What We Help With
          </button>
        )}
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Mobile: Stack layout */}
            <div className="lg:hidden text-center">
              <h1 className="text-2xl md:text-3xl mb-4 text-white">
                Pregnancy & Postpartum Support
              </h1>
              <p className="text-white/90 sm:w-2/3 mx-auto mb-6 sm:mb-12 text-sm sm:text-base">
                Pregnancy and the months after birth are big transitions – physically, emotionally and practically.
              </p>
              <div className="flex flex-col gap-3 mb-4 sm:w-1/2 mx-auto">
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => navigate('/appointments')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Appointment Types & Fees
                  </span>
                </Button>
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('pregnancy_hero_initial_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book an Initial Consultation & Treatment
                  </span>
                </Button>
              </div>
              <Button
                variant="outline"
                className="w-full border-white text-white hover:bg-white/10 sm:w-1/2 mx-auto"
                onClick={() => openBooking('pregnancy_hero_free_consult')}
              >
                <span className="inline-flex items-center gap-2">
                  <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                  Book a free 15-minute phone consult
                </span>
              </Button>
            </div>

            {/* Desktop: Side-by-side layout */}
            <div className="hidden lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
              <div>
                <h1 className="text-3xl lg:text-4xl mb-4 text-white">
                  Pregnancy & Postpartum Support
                </h1>
                <p className="text-white/90 text-base lg:text-lg">
                  Pregnancy and the months after birth are big transitions – physically, emotionally and practically.
                </p>
              </div>
              <div className="flex flex-col gap-3 mx-auto px-4">
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => navigate('/appointments')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Appointment Types & Fees
                  </span>
                </Button>
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('pregnancy_hero_initial_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book an Initial Consultation & Treatment
                  </span>
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-white text-white hover:bg-white/80"
                  onClick={() => openBooking('pregnancy_hero_free_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book a free 15-minute phone consult
                  </span>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Introduction Section */}
      <section id="introduction-section" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Pregnancy & Postpartum Support
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-4 text-left font-medium">
                Pregnancy and the months after birth are big transitions – physically, emotionally and practically.
              </p>
              <p className="text-gray-600 font-medium mb-4 text-left">
                This page is for people who are:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Pregnant and looking for gentle support at any stage</li>
                <li>Preparing for birth and wanting to feel as steady as possible going into labour</li>
                <li>Recovering after birth and adjusting to life with a baby</li>
                <li>Moving through pregnancy after loss or fertility treatment and needing extra emotional and nervous-system care</li>
              </ul>
              <p className="text-gray-600 text-left">
                Renee offers acupuncture and Chinese herbal medicine to support you through pregnancy and the postpartum months, alongside your medical and midwifery care.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who This Is For Section */}
      <section id="who-this-is-for" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Who This Is For
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left">
                People often come for pregnancy and postpartum care when they:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Are experiencing common pregnancy symptoms and would like more support</li>
                <li>Want a calm, steady place to pause during a demanding or anxious time</li>
                <li>Are preparing for birth and would like help with comfort, sleep and nervous-system regulation</li>
                <li>Are recovering after birth (vaginal or caesarean) and want to rebuild energy and steadiness</li>
                <li>Are trying to care for a new baby while dealing with disrupted sleep, big emotions and physical recovery</li>
                <li>Are pregnant again after previous fertility treatment, loss or a difficult birth and feel understandably cautious or on edge</li>
              </ul>
              <p className="text-gray-600 text-left">
                You don't need to have a specific diagnosis to seek support here. Care is always individualised and planned around your existing medical care.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Common Reasons People Seek Support Section */}
      <section id="common-reasons" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Common Reasons People Seek Support
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-4 text-left font-medium">
                Everyone's pregnancy is different, but some of the common reasons people come include:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Nausea and queasiness</li>
                <li>Tiredness, low energy or feeling "wiped out"</li>
                <li>Sleep difficulties and restlessness</li>
                <li>General aches and pains, including back, hip or pelvic discomfort</li>
                <li>Feelings of anxiety or being overwhelmed</li>
                <li>Preparing for birth and wanting to feel more settled in the lead-up</li>
                <li>Recovery from birth – soreness, fatigue, emotional swings, difficulty feeling "back in your body"</li>
                <li>Support during breastfeeding or feeding, especially when tired or run down</li>
                <li>Managing the "fourth trimester" – the early months when everything feels new</li>
              </ul>
              <p className="text-gray-600 text-left">
                For some people, the main focus is physical comfort. For others, it is emotional steadiness, processing a previous experience, or simply having a quiet, caring space that is just for them.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How Acupuncture & Chinese Medicine May Help Section */}
      <section id="how-acupuncture-helps" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              How Acupuncture & Chinese Medicine May Help in Pregnancy & Postpartum
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left">
                Within pregnancy and postpartum care, acupuncture and Chinese medicine are used to:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Support the body as it adapts to pregnancy and recovers after birth</li>
                <li>Work with the nervous system to help you feel more settled and grounded</li>
                <li>Pay attention to sleep, digestion and energy, which affect how you cope day to day</li>
                <li>Offer some relief and rest in the middle of a very full season of life</li>
              </ul>
              <p className="text-gray-600 text-left">
                Treatment is always tailored to your stage of pregnancy or postpartum. It is intended to sit <strong>alongside</strong>, not replace, the care you receive from your midwife, GP, obstetric team or child health nurse.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Support Across the Trimesters Section */}
      <section id="support-across-trimesters" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Support Across the Trimesters
            </h2>
            
            {/* Early Pregnancy */}
            <div className="mb-12">
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  Early Pregnancy
                </h3>
                <p className="text-gray-600 font-medium mb-4 text-left">
                  In early pregnancy, people commonly come for:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Nausea and queasiness</li>
                  <li>Tiredness and low energy</li>
                  <li>Worry and "waiting for the next scan" feelings</li>
                  <li>Adjusting to the reality of being pregnant, especially after fertility treatment or loss</li>
                </ul>
                <p className="text-gray-600 text-left">
                  Care at this stage focuses on gentleness – helping you cope with day-to-day symptoms, offering space to exhale, and supporting the nervous system while you move through this early, often tender, phase.
                </p>
              </div>
            </div>

            {/* Mid Pregnancy */}
            <div className="mb-12">
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  Mid Pregnancy
                </h3>
                <p className="text-gray-600 font-medium mb-4 text-left">
                  As pregnancy progresses, support may centre on:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Ongoing tiredness or disrupted sleep</li>
                  <li>Muscular tension, back discomfort, pelvic or rib aches</li>
                  <li>Digestion and bowel changes</li>
                  <li>Keeping you feeling as comfortable and steady as possible as your body changes</li>
                </ul>
                <p className="text-gray-600 text-left">
                  This can also be a good time to build a rhythm of care that supports you heading towards the later months of pregnancy.
                </p>
              </div>
            </div>

            {/* Late Pregnancy & Birth Preparation */}
            <div>
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  Late Pregnancy & Birth Preparation
                </h3>
                <p className="text-gray-600 font-medium mb-4 text-left">
                  In the final weeks, people may come for:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Support with general comfort and sleep</li>
                  <li>Help with feeling more settled and ready as birth approaches</li>
                  <li>Creating regular moments of calm and connection in the middle of appointments, planning and practical tasks</li>
                </ul>
                <p className="text-gray-600 text-left">
                  Treatment at this stage is always adjusted in line with your gestation, your obstetric or midwifery advice, and what feels appropriate for you. The focus is on helping you enter birth as resourced, informed and steady as possible.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Postpartum & the Fourth Trimester Section */}
      <section id="postpartum-fourth-trimester" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Postpartum & the Fourth Trimester
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-4 text-left font-medium">
                The postpartum period is a major physical and emotional transition, regardless of how birth unfolded.
              </p>
              <p className="text-gray-600 font-medium mb-4 text-left">
                People often seek support for:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Fatigue and feeling depleted</li>
                <li>Aches, pains and recovery after vaginal birth or caesarean section</li>
                <li>Sleep disturbance linked to feeding and caring for a baby</li>
                <li>Mood changes, tearfulness, irritability or feeling emotionally raw</li>
                <li>Digestive changes, appetite changes or bowel issues after birth</li>
                <li>The feeling of being "all used up" from doing a lot of caring and not much resting</li>
              </ul>
              <p className="text-gray-600 font-medium mb-4 text-left">
                Postpartum care with acupuncture and Chinese medicine may involve:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Gentle treatments aimed at supporting rest, recovery and circulation</li>
                <li>Attention to warmth, nourishment and realistic, small steps that fit into life with a baby</li>
                <li>Space to talk through how you are feeling physically and emotionally, in a non-judgemental way</li>
              </ul>
              <p className="text-gray-600 text-left">
                If you are under the care of your GP, obstetrician or another health professional for postpartum physical or mental health concerns, treatment will be planned with that in mind.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pregnancy After Loss or Fertility Treatment Section */}
      <section id="pregnancy-after-loss" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Pregnancy After Loss or Fertility Treatment
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-4 text-left font-medium">
                Pregnancy after loss or fertility treatment can feel very different to other pregnancies. Sometimes there is more worry, more watching, and less sense of ease.
              </p>
              <p className="text-gray-600 font-medium mb-4 text-left">
                People in this situation may come for:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Somewhere to bring the anxiety, rather than holding it all alone</li>
                <li>Support around scan dates, milestones and anniversaries</li>
                <li>Help with sleep, nervous-system regulation and grounding during a pregnancy that may feel emotionally complex</li>
              </ul>
              <p className="text-gray-600 text-left">
                Sessions can be a place to acknowledge what you have been through, while also gently supporting your body and mind in the present pregnancy.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section id="what-to-expect" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              What to Expect in a Pregnancy or Postpartum Appointment
            </h2>
            
            {/* Your First Visit */}
            <div className="mb-12">
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  Your First Visit
                </h3>
                <p className="text-gray-600 mb-4 text-left font-medium">
                  Your initial consultation and treatment is a longer appointment. Together you will:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Discuss your current stage of pregnancy or postpartum</li>
                  <li>Talk through your main concerns, symptoms and what you would like support with</li>
                  <li>Take a brief, relevant health history (including previous pregnancies or losses, where applicable)</li>
                  <li>Clarify what other care you are receiving, and any guidance given by your midwife, GP or obstetrician</li>
                  <li>Begin acupuncture treatment, and, if appropriate, discuss Chinese herbal medicine and simple, manageable self-care steps</li>
                </ul>
                <p className="text-gray-600 text-left">
                  You will not be asked to make big changes all at once. The aim is to understand your situation and agree on some gentle, realistic steps forward.
                </p>
              </div>
            </div>

            {/* Ongoing Care */}
            <div>
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  Ongoing Care
                </h3>
                <p className="text-gray-600 font-medium mb-4 text-left">
                  Follow-up appointments focus on:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Checking in on how you have been physically and emotionally since the last visit</li>
                  <li>Adjusting treatment as pregnancy progresses or as postpartum recovery unfolds</li>
                  <li>Reassessing how often it makes sense to come in, depending on your energy, family demands and other supports</li>
                  <li>Making small adjustments in response to new symptoms, milestones or challenges</li>
                </ul>
                <p className="text-gray-600 text-left">
                  Plans are flexible. You and Renee can increase or decrease the frequency of appointments as needed over time.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Safety & Working with Your Care Team Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Safety & Working with Your Care Team
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-4 text-left font-medium">
                Pregnancy and postpartum are times when clear communication and safety are especially important.
              </p>
              <p className="text-gray-600 font-medium mb-4 text-left">
                Renee will:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Always ask about your stage of pregnancy or postpartum at each visit</li>
                <li>Encourage you to follow the advice of your midwife, GP, obstetrician and other health professionals</li>
                <li>Refer you back to your primary care provider if something needs urgent or specific medical attention</li>
                <li>Work with, not against, any plans you have already made with your care team</li>
              </ul>
              <p className="text-gray-600 text-left">
                Acupuncture and Chinese medicine are used here as part of a broader network of care, not as a replacement for medical or midwifery support.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Is This Approach Right for You? Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Is This Approach Right for You?
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left">
                Pregnancy and postpartum support at Raintree may be a good fit if you are looking for:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8 text-left">
                <li>A calm, unhurried space that is just for you in the middle of a busy season</li>
                <li>Treatment that respects and works alongside your existing medical and midwifery care</li>
                <li>A practitioner who is experienced in women's health, fertility and pregnancy, and who takes time to listen</li>
                <li>Small, realistic steps rather than pressure to do everything perfectly</li>
              </ul>
              <p className="text-gray-600 font-medium mb-4 text-left">
                If you're unsure, you don't need to decide straight away.
              </p>
              <p className="text-gray-600 text-left">
                You're welcome to book a{' '}
                <button
                  onClick={() => openBooking('pregnancy_approach_free_consult')}
                  className="underline font-normal text-gray-600 hover:text-gray-800 cursor-pointer"
                >
                  free 15-minute phone consult
                </button>
                {' '}to talk briefly with Renee about your situation and any questions you might have.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Appointments, Locations & How to Start */}
      <SimpleLocationsSection onBook={(locationName) => {
        trackEvent('book_appointment_click', { location: `pregnancy_locations_${locationName.toLowerCase()}` })
        openBooking(`pregnancy_locations_${locationName.toLowerCase()}`)
      }} />

      {/* Next Steps CTA Section */}
      <section className="py-16 lg:py-32 bg-white px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Mobile: Stack layout */}
            <div className="lg:hidden text-center">
              <h2 className="text-2xl md:text-xl mb-10 text-[#3f4141]">
                Next steps
              </h2>
              <div className="mb-6">
                <p className="text-gray-600 mb-4">
                  For new pregnancy or postpartum patients, the best starting point is:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4 inline-block text-left max-w-lg mx-auto">
                  <li>
                    <strong>Initial Consultation & Treatment</strong> – a longer first appointment to take a clear history, understand your current needs and begin treatment.
                  </li>
                </ul>
                <p className="text-gray-600 max-w-lg mx-auto">
                  You can then decide together how often to continue and which clinic is most practical for you.
                </p>
              </div>
              <div className="flex flex-col gap-3 mb-4 sm:w-1/2 mx-auto">
                <Button
                  className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                  onClick={() => {
                    trackEvent('book_appointment_click', { location: 'pregnancy_cta_view_appointments' })
                    navigate('/appointments')
                  }}
                >
                  <span className="inline-flex items-center gap-2">
                    Appointment Types & Fees
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </Button>
                <Button
                  className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                  onClick={() => openBooking('pregnancy_cta_initial_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Initial Consult & Treatment
                  </span>
                </Button>
              </div>
              <Button
                variant="outline"
                className="w-full border-gray-600 text-gray-600 hover:bg-gray-50 sm:w-1/2 mx-auto"
                onClick={() => openBooking('pregnancy_cta_free_consult')}
              >
                <span className="inline-flex items-center gap-2">
                  <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                  Free 15-minute phone consult
                </span>
              </Button>
            </div>

            {/* Desktop: Title centered above two columns */}
            <div className="hidden lg:block">
              <h2 className="text-3xl lg:text-4xl mb-16 text-[#3f4141] text-center">
                Next steps
              </h2>
              <div className="lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
                <div>
                  <p className="text-gray-600 mb-4 text-base lg:text-md">
                    For new pregnancy or postpartum patients, the best starting point is:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4 text-base lg:text-md">
                    <li>
                      <strong>Initial Consultation & Treatment</strong> – a longer first appointment to take a clear history, understand your current needs and begin treatment.
                    </li>
                  </ul>
                  <p className="text-gray-600 text-base lg:text-md">
                    You can then decide together how often to continue and which clinic is most practical for you.
                  </p>
                </div>
                <div className="flex flex-col gap-3 mx-auto px-4 w-80">
                  <Button 
                    className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                    onClick={() => {
                      trackEvent('book_appointment_click', { location: 'pregnancy_cta_view_appointments' })
                      navigate('/appointments')
                    }}
                  >
                    <span className="inline-flex items-center gap-2">
                      Appointment Types & Fees
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </Button>
                  <Button 
                    className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                    onClick={() => openBooking('pregnancy_cta_initial_consult')}
                  >
                    <span className="inline-flex items-center gap-2">
                      <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                      Book Initial Consult & Treatment
                    </span>
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full border-gray-600 text-gray-600 hover:bg-gray-50"
                    onClick={() => openBooking('pregnancy_cta_free_consult')}
                  >
                    <span className="inline-flex items-center gap-2">
                      <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                      Free 15-minute phone consult
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

    </main>
  )
}

